import { useMemo } from 'react';
import { useLMAReferrals, useLMANonReferrals, useLMAProcessedReferrals } from './useLMA';
import { TAB_KEYS } from 'components/LeadManagement/constants';

interface TabConfig {
  hook: any;
  statusFilters: any;
}

interface TabRegistry {
  [key: string]: TabConfig;
}

interface UseCurrentTabProps {
  currentTabKey: string;
  referralStatusFilters?: any;
  nonReferralStatusFilters?: any;
  processedStatusFilters?: any;
}

interface CurrentTabData {
  data: any[];
  totalRecords: number;
  loading: boolean;
  error: string | null;
  currentPage: number;
  pageSize: number;
  filters: Record<string, string>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  dropdownData: any;
  dropdownLoading: boolean;
  dropdownError: string | null;
  loadData: () => void;
  loadDropdownData: () => void;
  applyFilters: (filters: Record<string, string | string[]>) => void;
  changePage: (page: number) => void;
  changePageSize: (pageSize: number) => void;
  changeSorting: (field: string, order: 'asc' | 'desc') => void;
  clearSorting: () => void;
}

/**
 * Custom hook to manage current tab state and operations
 * Eliminates all switch statements by using a registry pattern
 */
export const useCurrentTab = ({
  currentTabKey,
  referralStatusFilters,
  nonReferralStatusFilters,
  processedStatusFilters,
}: UseCurrentTabProps): CurrentTabData => {
  // Hook calls - must be called unconditionally (React rules of hooks)
  const referralsHook = useLMAReferrals(referralStatusFilters);
  const nonReferralsHook = useLMANonReferrals(nonReferralStatusFilters);
  const processedReferralsHook = useLMAProcessedReferrals(processedStatusFilters);

  // Registry mapping tab keys to their hook data
  const tabRegistry: TabRegistry = useMemo(
    () => ({
      [TAB_KEYS.REFERRALS]: {
        hook: referralsHook,
        statusFilters: referralStatusFilters,
      },
      [TAB_KEYS.NON_REFERRALS]: {
        hook: nonReferralsHook,
        statusFilters: nonReferralStatusFilters,
      },
      [TAB_KEYS.PROCESSED_REFERRALS]: {
        hook: processedReferralsHook,
        statusFilters: processedStatusFilters,
      },
    }),
    [
      referralsHook,
      nonReferralsHook,
      processedReferralsHook,
      referralStatusFilters,
      nonReferralStatusFilters,
      processedStatusFilters,
    ]
  );

  // Get current tab configuration
  const currentTabConfig = useMemo(() => {
    return tabRegistry[currentTabKey] || tabRegistry[TAB_KEYS.REFERRALS];
  }, [tabRegistry, currentTabKey]);

  // Extract hook data with generic names
  const currentTab = currentTabConfig.hook;

  // Return normalized interface with generic variable names
  return useMemo(
    () => ({
      data: currentTab.data || [],
      totalRecords: currentTab.totalRecords || 0,
      loading: currentTab.loading || false,
      error: currentTab.error || null,
      currentPage: currentTab.currentPage || 1,
      pageSize: currentTab.pageSize || 10,
      filters: currentTab.filters || {},
      sortBy: currentTab.sortBy,
      sortOrder: currentTab.sortOrder,
      dropdownData: currentTab.dropdownData || null,
      dropdownLoading: currentTab.dropdownLoading || false,
      dropdownError: currentTab.dropdownError || null,
      loadData:
        currentTab.loadReferrals ||
        currentTab.loadNonReferrals ||
        currentTab.loadProcessedReferrals,
      loadDropdownData: currentTab.loadDropdownData,
      applyFilters: currentTab.applyFilters,
      changePage: currentTab.changePage,
      changePageSize: currentTab.changePageSize,
      changeSorting: currentTab.changeSorting,
      clearSorting: currentTab.clearSorting,
    }),
    [currentTab]
  );
};

/**
 * Hook to load all tabs data on mount
 * Returns load functions for all tabs
 */
export const useLoadAllTabs = ({
  referralStatusFilters,
  nonReferralStatusFilters,
  processedStatusFilters,
}: Omit<UseCurrentTabProps, 'currentTabKey'>) => {
  const { loadDropdownData: loadReferralsDropdown } = useLMAReferrals(referralStatusFilters);
  const { loadDropdownData: loadNonReferralsDropdown } =
    useLMANonReferrals(nonReferralStatusFilters);
  const { loadDropdownData: loadProcessedReferralsDropdown } =
    useLMAProcessedReferrals(processedStatusFilters);

  return {
    loadAllTabs: () => {
      // loadReferrals();
      // loadNonReferrals();
      // loadProcessedReferrals();
      loadReferralsDropdown();
      loadNonReferralsDropdown();
      loadProcessedReferralsDropdown();
    },
  };
};
